import { AppShell, Burger, Divider, Group, NavLink, ScrollArea, Title } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconHome,
  IconUsers,
  IconReceipt2,
  IconUserCog,
  IconCash,
  IconShoppingBag,
  IconChartBar,
  IconBuildingCommunity,
} from '@tabler/icons-react';
import { NavLink as RouterNavLink, Outlet, useLocation } from 'react-router-dom';
import { UserMenu } from '../components/UserMenu';
import { GroupPicker } from '../components/GroupPicker';
import { useAuth } from '../auth/AuthProvider';

/**
 * Financial / pta_leader layout — sidebar (operatív munkához).
 * A FEA AppLayout mintáját követi, csak a menüpontok mások.
 */
export function FinancialLayout() {
  const [opened, { toggle, close }] = useDisclosure();
  const location = useLocation();
  const { activeRole, activeGroupId } = useAuth();

  // "Pénztárosok" menüpont csak leader-nek vagy felsőbb adminnak — cashier nem grantelhet.
  const canManageCashiers =
    activeRole === 'pta_leader' ||
    activeRole === 'institution_admin' ||
    activeRole === 'country_admin' ||
    activeRole === 'systemadmin' ||
    activeRole === 'applicationadmin';

  // A "Diákok" csak akkor működik, ha van aktív csoport.
  const studentsPath = activeGroupId ? `/app/groups/${activeGroupId}/students` : '/app/my-groups';

  return (
    <AppShell
      header={{ height: 56 }}
      navbar={{ width: 240, breakpoint: 'sm', collapsed: { mobile: !opened } }}
      padding="md"
    >
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between" wrap="nowrap">
          <Group wrap="nowrap">
            <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" />
            <Title order={4}>PTA</Title>
            <GroupPicker />
          </Group>
          <UserMenu />
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p="xs">
        <ScrollArea>
          <NavLink
            component={RouterNavLink}
            to="/app"
            end
            label="Kezdőlap"
            leftSection={<IconHome size={18} />}
            active={location.pathname === '/app'}
            onClick={close}
          />
          <NavLink
            component={RouterNavLink}
            to="/app/my-groups"
            label="Saját csoportjaim"
            leftSection={<IconBuildingCommunity size={18} />}
            active={location.pathname.startsWith('/app/my-groups')}
            onClick={close}
          />
          <Divider my="sm" label="Csoport" labelPosition="left" />
          <NavLink
            component={RouterNavLink}
            to={studentsPath}
            label="Diákok"
            leftSection={<IconUsers size={18} />}
            active={location.pathname.startsWith('/app/groups/') && location.pathname.endsWith('/students')}
            onClick={close}
          />
          <NavLink
            component={RouterNavLink}
            to="/app/requirements"
            label="Előírások"
            leftSection={<IconReceipt2 size={18} />}
            active={location.pathname.startsWith('/app/requirements')}
            onClick={close}
          />
          <NavLink
            component={RouterNavLink}
            to="/app/payments"
            label="Befizetések"
            leftSection={<IconCash size={18} />}
            active={location.pathname.startsWith('/app/payments')}
            onClick={close}
          />
          <NavLink
            component={RouterNavLink}
            to="/app/expenses"
            label="Kiadások"
            leftSection={<IconShoppingBag size={18} />}
            active={location.pathname.startsWith('/app/expenses')}
            onClick={close}
          />
          <NavLink
            component={RouterNavLink}
            to="/app/reports"
            label="Riportok"
            leftSection={<IconChartBar size={18} />}
            active={location.pathname.startsWith('/app/reports')}
            onClick={close}
          />
          {canManageCashiers && (
            <NavLink
              component={RouterNavLink}
              to="/app/cashier-grants"
              label="Pénztárosok"
              leftSection={<IconUserCog size={18} />}
              active={location.pathname.startsWith('/app/cashier-grants')}
              onClick={close}
            />
          )}
        </ScrollArea>
      </AppShell.Navbar>

      <AppShell.Main>
        <Outlet />
      </AppShell.Main>
    </AppShell>
  );
}
